import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory, readonlyHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { useMemo } from "react";
import { ensureInteger } from "../../common/utils/generalUtils";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { canSuperViewMembers } from "./memberSuperSlice";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { isPrimary } from "./memberUtils";

const prepareArray = data => ensureArray(data).map(member => ({
    ...member,
    name: member.preferredname ?? member.firstname,
    fullname: (member.preferredname ?? member.firstname) + ' ' + member.lastname,
    // TODO remove once customtables is deprecated needed for ACES computed fields like classification
    customfields: { ...member.customfields, ...member.customtables },
}))

export const memberMgrSlice = createSlice({
    name: 'members',
    initialState: {
        data: {},
        inactiveMembers: {},
    },
    reducers: {
        setActive: (state, action) => {
            // we can do the following because we always fetch all the members
            state.data = {};
            prepareArray(action.payload).forEach(m => state.data[m.id] = m);
        },
        setInactive : (state, action) => {
            state.inactiveMembers = {};
            prepareArray(action.payload).forEach(m => state.inactiveMembers[m.id] = m);
        },
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            prepareArray(action.payload.data).forEach(m => {
                const id = ensureInteger(m.id)
                if (m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m;
                } else {
                    state.data[id] = m;
                    delete state.inactiveMembers[id];
                }
                toProcess.delete(m.id);
            });
            toProcess.forEach(id => {
                delete state.data[id];
                delete state.inactiveMembers[id];
            });
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(m => {
                const id = ensureInteger(m.id);
                if (m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m;
                } else {
                    state.data[id] = m;
                    delete state.inactiveMembers[id];
                }
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(m => {
                const id = ensureInteger(m.id);
                if (state.data[id] && m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m
                }
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staff_one', action, data => ({ name: `${data.firstname} ${data.lastname}`,
    id: data.id 
}));
const visualizeFailure = verb => DefaultFailureAction(verb, 'staff_one');

const fetchAllActiveMembers = network => network.get('/api/v2/members/active', memberMgrSlice.actions.setActive, visualizeFailure('get'));
const fetchAllInactiveMembers = network => network.get('/api/v2/members/inactive' , memberMgrSlice.actions.setInactive, visualizeFailure('get')); 
const fetchMember = aggregatingFetch(
    id => network => network.post('/api/v2/members', [id], data => memberMgrSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/members', Array.from(ids), data => memberMgrSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);
// #endregion

// #region selectors
const selectMemberData = state => state.members.data;
const selectAllMemberData = state => state.members;
const selectMembersById = ids => createSelector([selectAllMemberData], data => ids.reduce((res, id) => { const m = data.data[id] ?? data.inactiveMembers[id]; if (m) { res.push(m) } return res; }, []));
const selectMemberById = id => createSelector([selectAllMemberData], data => data.data[id] ?? data.inactiveMembers[id]);
const selectAllActiveMembers = createSelector([selectMemberData], (members) => Object.values(members));
const selectInactiveMemberData = state => state.members.inactiveMembers;
const selectAllInactiveMembers = createSelector([selectInactiveMemberData], (members) => Object.values(members));
const selectAllActiveMembersMap = createSelector([selectMemberData], data => data);
const selectMemberMapById = ids => createSelector([selectMemberData], data => {
    return ids.reduce((acc, id) => {
        const memberData = data[id]
        if (memberData) {
            acc[id] = memberData
        } 
        return acc;
    }, {});
});
// #endregion

const listen = {
    memberLimitedCreated: (member, { dispatch, websocket }) => {
        markFetched(websocket, 'member', member.id);
        dispatch(memberMgrSlice.actions.addOrUpdate(member))
    },
    memberLimitedUpdated: (member, { dispatch }) => dispatch(memberMgrSlice.actions.addOrUpdate(member)),
    memberLimitedRemoved: (member, { dispatch }) => dispatch(memberMgrSlice.actions.remove(member))
}

// #region permissions
const limitedRoles = ['EPCR manager', 'EPCR auditor', 'EPCR reviewer', 'meeting manager', 'meeting auditor', 'event manager', 'event auditor', 'template manager', 'template auditor', 'rostering manager', 'rostering auditor', 'recurring rostering manager', 'recurring rostering auditor', 'equipment manager', 'equipment auditor','timesheet manager','timesheet auditor','accreditation auditor'];
const canViewMembers = (credentials) => canSuperViewMembers(credentials) || credentials.hasRole(limitedRoles);
export const canViewMember = (credentials, member) => canSuperViewMembers(credentials, member) || credentials.hasRole(limitedRoles, member ? { office: member.office, client: member.default_client, location: member.default_location } : undefined);
export const canRouteMember = ({credentials}) => canViewMembers(credentials);

// #endregion

const optionsMultiple = { condition: canViewMembers }
const optionsSingle = { condition: canViewMembers, defaultValue: null}

const initMemberByID = initMapFactory(fetchMember, listen);
registerEntityBasedFetch(initMemberByID, initMemberByID);

const initMultiMemberByID = initMultiMapFactory(initMemberByID, listen);
const initActiveMembers = queryMapFactory(fetchAllActiveMembers, 'id', initMemberByID, listen, { allowEmpty: true, name: 'activeMgrMembers', })();
const initInactiveMembers = queryMapFactory(fetchAllInactiveMembers, 'id', initMemberByID, listen, { allowEmpty: true, name: 'inactiveMgrMewmbers'})();
export const [useMgrAllActiveMembers] = dataHookWithCredentialsFactory(selectAllActiveMembers, initActiveMembers, optionsMultiple);
export const useMgrPrimaryActiveMembers = () => {
    const clientConfig = useClientConfig();
    const members = useMgrAllActiveMembers();
    return useMemo(() => members.filter(m => isPrimary(m, clientConfig)), [members, clientConfig]);
}
export const [useMgrAllInactiveMembers] = dataHookWithCredentialsFactory(selectAllInactiveMembers, initInactiveMembers, optionsMultiple);
export const [useMgrMembers] = dataHookWithCredentialsFactory(selectMembersById, initMultiMemberByID, optionsMultiple);
export const [useMgrMember, realizeMgrMember] = dataHookWithCredentialsFactory(selectMemberById, initMemberByID, optionsSingle);
export const realizeMgrMembers = async (context, ids) => await Promise.all(ids.map(id => realizeMgrMember(context, id)));
export const [useMgrMembersMap] = dataHookWithCredentialsFactory(selectMemberMapById, initMultiMemberByID, optionsMultiple);
export const [useMgrAllActiveMemberMap] = dataHookWithCredentialsFactory(selectAllActiveMembersMap, initActiveMembers, optionsMultiple);
export const useMgrPrimaryActiveMemberMap = () => {
    const clientConfig = useClientConfig();
    const allMap = useMgrAllActiveMemberMap();
    const map = useMemo(() => {
        const map = { ...allMap }
        for (const id of Object.keys(map)) {
            if (!isPrimary(map[id], clientConfig)) {
                delete map[id];
            }
        }
        return map;
    }, [allMap, clientConfig]);
    return map;
}

export const useMgrAllMembers = () => {
    const activeMembers = useMgrAllActiveMembers();
    const inactiveMembers = useMgrAllInactiveMembers();

    return useMemo(() => [...activeMembers, ...inactiveMembers], [activeMembers, inactiveMembers]);
}

export const [useReadMgrMember] = readonlyHookWithCredentialsFactory(selectMemberById, initMemberByID, optionsSingle);
export const [useReadMgrMembers] = readonlyHookWithCredentialsFactory(selectMembersById, initMultiMemberByID, optionsMultiple);
export const [useReadMgrAllActiveMembers] = readonlyHookWithCredentialsFactory(selectAllActiveMembers, initActiveMembers, optionsMultiple);
export const [useReadMgrAllActiveMembersMap] = readonlyHookWithCredentialsFactory(selectAllActiveMembersMap, initActiveMembers, optionsMultiple);

const reducer = { name: memberMgrSlice.name, reducer: memberMgrSlice.reducer }
export default reducer;